import { useState } from 'react';
import { toast } from '../components/toast';
import { get, put } from '../api';
import { Problems } from '../components/Field';
import { labelOf } from '../components/HistoryTable';
import { describe } from '../errors';
import { useLoad } from '../hooks';

interface Column {
  name: string;
  audited: boolean;
  override: boolean | null;
}
interface Table {
  table: string;
  label: string;
  enabled: boolean;
  columns: Column[];
}

/** Choose which records keep a field-by-field history, and which fields within them. Nothing is recorded by default. */
export function AuditSettings() {
  const [tick, setTick] = useState(0);
  const loaded = useLoad(() => get<{ tables: Table[] }>('/api/settings/audit'), [tick]);
  const tables = Array.isArray(loaded.data?.tables) ? loaded.data!.tables : [];
  if (loaded.loading && !loaded.data) return <p>Loading…</p>;
  return (
    <section className="stack narrow" aria-labelledby="audit-h">
      <h2 id="audit-h">Change history</h2>
      <p className="muted">
        Keep a record of who changed which field, and when. It is off until you switch it on, and
        only fields that actually changed are stored. Sensitive fields such as access notes and
        client references are never recorded.
      </p>
      {loaded.error && (
        <p role="alert" className="error">
          {loaded.error}
        </p>
      )}
      {tables.map((t) => (
        <TableCard key={t.table} t={t} reload={() => setTick((n) => n + 1)} />
      ))}
    </section>
  );
}

function TableCard(props: { t: Table; reload(): void }) {
  const { t } = props;
  const [enabled, setEnabled] = useState(t.enabled);
  const [audited, setAudited] = useState<Record<string, boolean>>(
    Object.fromEntries(t.columns.map((c) => [c.name, c.audited])),
  );
  const [problems, setProblems] = useState<string[]>([]);

  // A field that matches the table's own setting simply follows it; only a field that differs is a choice of its own.
  const overrides = () =>
    Object.fromEntries(
      t.columns.map((c) => [
        c.name,
        audited[c.name] === enabled ? null : (audited[c.name] ?? null),
      ]),
    );

  function toggleTable(on: boolean) {
    setEnabled(on);
    // Fields that were following the table follow it to the new setting.
    setAudited(
      Object.fromEntries(
        t.columns.map((c) => [c.name, c.override === null ? on : (audited[c.name] ?? on)]),
      ),
    );
  }

  async function save() {
    setProblems([]);
    try {
      await put('/api/settings/audit', { table: t.table, enabled, columns: overrides() });
      toast('Saved.');
      props.reload();
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  return (
    <fieldset className="audit-card">
      <legend>{t.label}</legend>
      <Problems problems={problems} />
      <label className="check">
        <input type="checkbox" checked={enabled} onChange={(e) => toggleTable(e.target.checked)} />
        Record changes to {t.label.toLowerCase()}
      </label>
      <details>
        <summary>
          Choose fields ({t.columns.filter((c) => audited[c.name]).length} of {t.columns.length})
        </summary>
        <ul className="plain cols">
          {t.columns.map((c) => (
            <li key={c.name}>
              <label className="check">
                <input
                  type="checkbox"
                  checked={audited[c.name] ?? false}
                  onChange={(e) => setAudited({ ...audited, [c.name]: e.target.checked })}
                />
                {labelOf(c.name)}
              </label>
            </li>
          ))}
        </ul>
      </details>
      <div className="row start">
        <button type="button" onClick={() => void save()}>
          Save {t.label.toLowerCase()} history settings
        </button>
      </div>
    </fieldset>
  );
}
